/**
 * Pan/zoom transform — render-exempt file (test-spec §7.2). Screen-space
 * only; all game math stays in shared/grid (world coordinates).
 */

import type { Application, Container, FederatedPointerEvent } from "pixi.js";

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;

export function attachViewport(app: Application, world: Container): void {
  app.stage.eventMode = "static";
  app.stage.hitArea = app.screen;

  let panning = false;
  let lastX = 0;
  let lastY = 0;

  app.stage.on("pointerdown", (event: FederatedPointerEvent) => {
    // Token sprites stop propagation; reaching the stage means background.
    panning = true;
    lastX = event.globalX;
    lastY = event.globalY;
  });
  app.stage.on("pointermove", (event: FederatedPointerEvent) => {
    if (!panning) return;
    world.position.x += event.globalX - lastX;
    world.position.y += event.globalY - lastY;
    lastX = event.globalX;
    lastY = event.globalY;
  });
  const stopPan = () => {
    panning = false;
  };
  app.stage.on("pointerup", stopPan);
  app.stage.on("pointerupoutside", stopPan);

  app.canvas.addEventListener(
    "wheel",
    (event: WheelEvent) => {
      event.preventDefault();
      const factor = event.deltaY < 0 ? 1.15 : 1 / 1.15;
      const next = Math.min(Math.max(world.scale.x * factor, MIN_ZOOM), MAX_ZOOM);
      const applied = next / world.scale.x;
      // Zoom around the cursor: keep the world point under it fixed.
      const rect = app.canvas.getBoundingClientRect();
      const cursorX = event.clientX - rect.left;
      const cursorY = event.clientY - rect.top;
      world.position.x = cursorX - (cursorX - world.position.x) * applied;
      world.position.y = cursorY - (cursorY - world.position.y) * applied;
      world.scale.set(next);
    },
    { passive: false },
  );
}
